import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputComponent } from "@components/input/input.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { DisabledControlDirective } from "~app/common/directives/disabled-control/disabled-control.directive";
import { AnvandareEditDTO } from "~app/common/models/anvandare/AnvandareEditDTO";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { FeatureService } from "~app/common/services/feature.service";
import { environment } from "~environments/environment";
import { AnvandareEditService } from "../anvandare-edit.service";

@Component({
    selector: "rask-redigera-anvandare",
    templateUrl: "./redigera-anvandare.component.html",
    styleUrls: ["./redigera-anvandare.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        TabsComponent,
        TabComponent,
        CheckboxComponent,
        SaveButtonComponent,
        ButtonComponent,
        DisabledControlDirective
    ]
})
export class RedigeraAnvandareComponent implements OnInit {
    modal = inject(NgbActiveModal);

    protected editForm = new UntypedFormGroup({});
    protected user: AnvandareEditDTO;
    private readonly anvandareEditService = inject(AnvandareEditService);
    private readonly busyService = inject(BusyService);
    private readonly dirtyForm = inject(DirtyFormService);
    private readonly featureService = inject(FeatureService);
    private readonly toastService = inject(ToastService);

    private userId: string;

    dedotify(input: string): string {
        return input.replace(".", "_");
    }

    dirtyCheckCallback(result: IDirtyFormResult) {
        if (result.isSkipSave) {
            this.editForm.markAsPristine();
            this.dismiss();
        }
        if (result.isSave) {
            this.save();
        }
    }

    dismiss() {
        if (this.dirtyForm.canClose()) {
            this.modal.dismiss();
        }
    }

    mapFromForm(form: UntypedFormGroup, user: AnvandareEditDTO) {
        user.distriktskod = form.get("distriktskod").value;
        user.email = form.get("email").value;
        user.firstName = form.get("firstName").value;
        user.lastName = form.get("lastName").value;
        user.phoneNumber = form.get("phoneNumber").value;
        user.userName = form.get("userName").value;
        user.roles.forEach(r => {
            const ctrl = form.get(this.dedotify(r.name));
            r.selected = ctrl.value;
        });
        if(user.featureSettings) {
            user.featureSettings.forEach(r => {
                const ctrl = form.get(this.dedotify(r.name));
                r.selected = ctrl.value;
            });
        }
    }

    mapFromModel(user: AnvandareEditDTO) {
        this.editForm = new UntypedFormGroup({
            sveaEmployee: new UntypedFormControl(user.sveaEmployee),
            firstName: new UntypedFormControl(user.firstName),
            lastName: new UntypedFormControl(user.lastName),
            userName: new UntypedFormControl(user.userName),
            email: new UntypedFormControl(user.email),
            phoneNumber: new UntypedFormControl(user.phoneNumber),
            distriktskod: new UntypedFormControl(user.distriktskod)
        });
        user.roles.forEach(role => {
            this.editForm.addControl(this.dedotify(role.name), new UntypedFormControl(role.selected));
        });
        if(user.featureSettings) {
            user.featureSettings.forEach(feature => {
                this.editForm.addControl(this.dedotify(feature.name), new UntypedFormControl(feature.selected));
            });
        }
        this.editForm.markAsPristine();
    }

    ngOnInit() {
        this.anvandareEditService.getAnvandareForEdit(this.userId).subscribe(result => {
            this.user = result;
            this.mapFromModel(this.user);
            this.dirtyForm.onDirty(this.editForm, this.save, d => this.dirtyCheckCallback(d));
        });
    }

    save() {
        if (this.editForm.valid) {
            this.mapFromForm(this.editForm, this.user);
            const worker = this.busyService.addWork(this.anvandareEditService.saveAnvandare(this.user));
            worker.subscribe(result => {
                this.editForm.markAsPristine();
                this.toastService.add("Ändringarna sparades");
                this.featureService.refreshClientFeatures();
                this.modal.close(result);
            });
        }
    }

    setUserId(userId: string) {
        this.userId = userId;
    }

    isUppdateraRaskEntraGrupperAllowed(): boolean {
        return environment.uppdateraRaskEntraGrupper === "true";
    }
}
